/**
 * The lab's calendar day.
 *
 * Every instant on the wire is UTC, section 7.9, and every day the lab counts
 * is a day in Dubai. The two disagree for four hours out of every twenty-four,
 * which is enough to file a case finished at ten at night under tomorrow and to
 * make a "today" report come back empty on a server that is not in Dubai.
 * Section 3.3 of the request of 20 September 2026 names that as the third thing
 * to check when the productivity report shows nothing.
 *
 * A fixed offset rather than a time zone database: Dubai keeps no daylight
 * saving and never has, so +04:00 is the whole rule, and arithmetic that cannot
 * consult a zone table is arithmetic the .NET side can copy in one line
 * (`TimeZoneInfo.FindSystemTimeZoneById("Arabian Standard Time")`, or simply
 * `AddHours(4)`).
 *
 * The lab's weekend is Friday and Saturday, which `workingDays.ts` already
 * states; `labWorkingDaysIn` below reads it from there rather than restating it.
 */

import { WEEKEND_DAYS } from './workingDays';

/** Dubai is four hours ahead of UTC, all year. */
export const LAB_UTC_OFFSET_MINUTES = 4 * 60;

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

/**
 * The lab calendar day an instant fell on, YYYY-MM-DD.
 *
 * Not `iso.slice(0, 10)`, which is the UTC day: a case finished at half past
 * midnight in Dubai is still the previous day in UTC.
 */
export function labDayOf(instant: string | number | Date): string {
  const ms = instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
  return new Date(ms + LAB_UTC_OFFSET_MINUTES * MS_PER_MINUTE).toISOString().slice(0, 10);
}

/** The instant a lab day begins, as UTC. `2026-09-20` starts at 2026-09-19T20:00:00Z. */
export function labDayStart(day: string): Date {
  return new Date(Date.parse(`${day}T00:00:00Z`) - LAB_UTC_OFFSET_MINUTES * MS_PER_MINUTE);
}

/**
 * The instant a lab day ends, as UTC, the last millisecond of it rather than the
 * first of the next. Both ends of a report range are inclusive, so a task that
 * closed at 23:59:59 in Dubai is in that day and not in the following one.
 */
export function labDayEnd(day: string): Date {
  return new Date(labDayStart(day).getTime() + MS_PER_DAY - 1);
}

/**
 * The first moment after an instant at which the lab's day ends: `endsAt` is
 * "HH:mm" on the lab's clock, and the answer is that time on the instant's own
 * lab day, or on the next day when the instant is already past it. Request of
 * 25 September 2026: every clock still running is stopped then, and the
 * productivity report counts no run past it.
 */
export function labDayEndAfter(instant: number, endsAt: string): number {
  // "HH:mm", as the settings store it.
  const hours = Number(endsAt.slice(0, 2));
  const minutes = Number(endsAt.slice(3, 5));
  const sameDay =
    labDayStart(labDayOf(instant)).getTime() +
    hours * 60 * MS_PER_MINUTE +
    minutes * MS_PER_MINUTE;
  return sameDay > instant ? sameDay : sameDay + MS_PER_DAY;
}

/**
 * When a run begun at `start` stops, and the staff report stops counting it:
 * the day end for a run begun in the working day; for one begun after it, the
 * after-hours cut-off or the next day end, whichever comes first. Null when the
 * lab has no day end. The backend's LabDayEnd.CutOff is the same rule. Section
 * 17 of the review of 2 October 2026.
 */
export function runCutOff(
  start: number,
  dayEndsAt: string | null,
  afterHoursEndsAt: string | null,
): number | null {
  if (dayEndsAt === null) return null;
  const dayEnd = labDayEndAfter(start, dayEndsAt);
  if (afterHoursEndsAt === null) return dayEnd;

  // Begun before today's day end: a working-day run.
  const todaysEnd = labDayEndAfter(labDayStart(labDayOf(start)).getTime(), dayEndsAt);
  if (start < todaysEnd) return dayEnd;

  return Math.min(labDayEndAfter(start, afterHoursEndsAt), dayEnd);
}

/** The lab day `days` before another. Arithmetic on the day alone, never on a clock. */
/**
 * How many whole lab days a case has been in the lab: none on the day it
 * arrived, one the day after. The lab's request of 11 October 2026, for a
 * column of the case lists. Null when the moment it arrived is not known, and
 * never negative: a clock a minute ahead of the lab's is still today.
 */
export function daysInLab(receivedAt: string | null | undefined, today: string): number | null {
  if (receivedAt === null || receivedAt === undefined || receivedAt === '') return null;
  const arrived = Date.parse(`${labDayOf(receivedAt)}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  return Math.max(0, Math.round((now - arrived) / 86_400_000));
}

export function labDaysBefore(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) - days * MS_PER_DAY).toISOString().slice(0, 10);
}

/** Every lab day from `from` to `to`, both ends included, in order. */
export function labDaysBetween(from: string, to: string): string[] {
  const last = Date.parse(`${to}T00:00:00Z`);
  const days: string[] = [];
  for (let at = Date.parse(`${from}T00:00:00Z`); at <= last; at += MS_PER_DAY) {
    days.push(new Date(at).toISOString().slice(0, 10));
  }
  return days;
}

/**
 * How many of those days the lab works. Friday and Saturday are its weekend, so
 * a seven day range is five working days and a report's utilisation is measured
 * against those rather than against the whole week.
 *
 * The weekday is read in UTC on purpose. The day string is already the lab's,
 * and `isWorkingDay` reads a Date in the machine's own zone, which would move
 * the weekday for a reader far enough from Greenwich. The weekend itself still
 * comes from `WEEKEND_DAYS`, so there is one statement of which days those are.
 */
export function labWorkingDaysIn(days: readonly string[]): number {
  return days.filter((day) => !WEEKEND_DAYS.includes(new Date(`${day}T00:00:00Z`).getUTCDay()))
    .length;
}

/**
 * The Monday the lab's week of a day begins on, as a lab day. The request of
 * 6 October 2026 (fifth), section 1: "this week" on the Doctors and Patients
 * pages is the calendar week from Monday, not the last seven days. The weekday
 * is read in UTC for the reason `labWorkingDaysIn` gives: the day string is
 * already the lab's.
 */
export function labWeekStart(day: string): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  // Sunday is 0, so it is six days after its Monday.
  return labDaysBefore(day, (weekday + 6) % 7);
}

/** The first day of the calendar month a lab day is in: "this month" on the same two pages. */
export function labMonthStart(day: string): string {
  return `${day.slice(0, 8)}01`;
}
